import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  mergeMap,
  Observable,
  pairwise,
  pipe,
  tap,
  UnaryFunction
} from 'rxjs';
import { ChunksCache } from './helpers';
import { calculateChunkOffset } from './helpers/calculate-chunk-offset.function';
import { PagedActionsTypesSet, PagedGridActions } from './paged-grid.actions';
import { PagedGridSelectors } from './selectors';
import { SessionCacheService } from '@anecdotes/utils';

@Injectable()
export class PagedGridEffects {
  private gridInstanceIds: Set<string>;
  private readonly sessionCacheService = inject(SessionCacheService);
  private readonly actions = inject(Actions);
  private readonly store = inject(Store);

  private chunkCacheServices = new Map<string, ChunksCache>();

  instanceIds$ = createEffect(
    () => {
      return this.store.pipe(
        select(PagedGridSelectors.selectInstanceIds),
        distinctUntilChanged(undefined, (instanceIds) => instanceIds.join()),
        tap((instanceIds) => (this.gridInstanceIds = new Set(instanceIds))),
      );
    },
    { dispatch: false },
  );

  init$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(PagedGridActions.Init),
        delay(0), // this delay is required
        tap((action) => {
          this.disposeCacheService(action.instanceId);
          this.createCacheService(action.instanceId);
          this.loadChunkAsync({
            instanceId: action.instanceId,
            chunkIndex: 0
          });
        }),
      ),
    { dispatch: false },
  );

  dispose$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(PagedGridActions.Dispose),
        tap((action) => this.disposeCacheService(action.instanceId)),
      ),
    { dispatch: false },
  );

  selectPage$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(PagedGridActions.SelectPage),
        this.filterByInstanceId(),
        mergeMap((action) => this.loadChunkAsync({ instanceId: action.instanceId, chunkIndex: 0 })),
      ),
    { dispatch: false },
  );

  chunkLoaded$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(PagedGridActions.ChunkLoaded),
        this.filterByInstanceId(),
        tap((action) =>
          this.store.dispatch(
            PagedGridActions.SetChunk({
              instanceId: action.instanceId,
              chunkIndex: action.chunkIndex,
              rows: action.rows,
            }),
          ),
        ),
        mergeMap(async (action) => {
          try {
            await this.chunkCacheServices.get(action.instanceId)?.writeAsync(action.chunkIndex, action.rows);
          } catch {
            // we should do just nothing in case of failure during caching
            // in case of cache failure, the current chunk will be attempted to load again from the server
          }
        })
      ),
    { dispatch: false },
  );

  reload$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(PagedGridActions.Reload),
        debounceTime(100),
        this.filterByInstanceId(),
        mergeMap(async (action) => {
          try {
            await this.chunkCacheServices.get(action.instanceId)?.clearCacheIfExistsAsync();
          } catch {
            // nothing to do
            // in case of error, we just need to proceed
          }

          return action;
        }),
        mergeMap((action) => this.loadChunkAsync({ instanceId: action.instanceId, chunkIndex: 0 })),
      ),
    { dispatch: false },
  );

  sort$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(PagedGridActions.ChangeSortColumn),
        this.filterByInstanceId(),
        mergeMap(async (action) => {
          await this.chunkCacheServices.get(action.instanceId).clearCacheIfExistsAsync();
          await this.loadChunkAsync({ instanceId: action.instanceId, chunkIndex: 0 });
        }),
      ),
    { dispatch: false },
  );

  cleanNotVisibleChunks$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(PagedGridActions.SetDisplayedRange),
        this.filterByInstanceId(),
        distinctUntilChanged(
          (fst, scnd) =>
            `${fst.visibleRange.startChunkIndex}-${fst.visibleRange.endChunkIndex}` ===
            `${scnd.visibleRange.startChunkIndex}-${scnd.visibleRange.endChunkIndex}`,
        ),
        tap((action) =>
          this.store.dispatch(
            PagedGridActions.CleanNotVisibleChunks({
              instanceId: action.instanceId,
              visibleRange: action.visibleRange,
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  visibleRangeChange$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(PagedGridActions.SetDisplayedRange),
        debounceTime(200),
        this.filterByInstanceId(),
        pairwise(),
        mergeMap(async ([previousAction, currentAction]) => {
          const isScrollDown =
            previousAction.visibleRange.startSubrowIndex - currentAction.visibleRange.startSubrowIndex < 0;

          const state = await firstValueFrom(
            this.store.pipe(select(PagedGridSelectors.selectPreviewState(currentAction.instanceId))),
          );

          const startIndex = isScrollDown
            ? currentAction.visibleRange.startChunkIndex
            : Math.max(0, currentAction.visibleRange.startChunkIndex - state.chunksBuffer);
          const endIndex = isScrollDown
            ? currentAction.visibleRange.endChunkIndex + state.chunksBuffer
            : currentAction.visibleRange.endChunkIndex;

          for (let chunkIndex = startIndex; chunkIndex <= endIndex; chunkIndex++) {
            if (chunkIndex > state.pageInfo.maxChunksInCurrentPage - 1) {
              break;
            }

            if (state.chunks[chunkIndex]?.loaded || state.chunks[chunkIndex]?.loading) {
              continue;
            }

            this.loadChunkAsync({ instanceId: currentAction.instanceId, chunkIndex });
          }
        }),
      ),
    { dispatch: false },
  );

  private async loadChunkAsync(params: { instanceId: string; chunkIndex: number }): Promise<void> {
    if (await this.tryLoadFromCacheAsync(params)) {
      return;
    }

    await this.dispatchLoadChunkAsync(params);
  }

  /**
   * Tries to load chunk from cache.
   * Returns true if successfully read from cache.
   * In case when chunk is not in cache or in case of cache read failure, returns false
   * @param params
   * @returns 
   */
  private async tryLoadFromCacheAsync(params: { instanceId: string; chunkIndex: number }): Promise<boolean> {
    try {
      if (!this.chunkCacheServices.has(params.instanceId)) {
        return false;
      }
  
      const cachedRows = await this.chunkCacheServices.get(params.instanceId).readAsync(params.chunkIndex);

      if (cachedRows) {
        this.store.dispatch(
          PagedGridActions.SetChunk({
            instanceId: params.instanceId,
            chunkIndex: params.chunkIndex,
            rows: cachedRows,
          }),
        );
  
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  /**
   * Hands over to consumer to load chunk data (from server or whatever)
   * @param params 
   * @returns 
   */
  private async dispatchLoadChunkAsync(params: { instanceId: string; chunkIndex: number }): Promise<void> {
    const state = await firstValueFrom(
      this.store.pipe(select(PagedGridSelectors.selectPreviewState(params.instanceId))),
    );

    if (!state?.pageInfo) {
      return;
    }

    const chunkOffset = calculateChunkOffset({
      chunkIndex: params.chunkIndex,
      currentPageIndex: state.pageInfo.currentPageIndex,
      rowsPerChunk: state.pageInfo.rowsPerChunk,
      rowsPerPage: state.pageInfo.rowsPerPage,
    });

    if (chunkOffset > state.pageInfo.totalRows) {
      return;
    }

    this.store.dispatch(
      PagedGridActions.LoadChunk({
        instanceId: params.instanceId,
        chunkIndex: params.chunkIndex,
        limit: state.pageInfo.rowsPerChunk,
        offset: chunkOffset,
        sortColumn: state.sortColumn,
        sortDirection: state.sortDirection,
      }),
    );
  }

  /**
   * This pipe is to filter paged grid actions for destroyed or not created paged grid instances.
   * Also, it bypases all other actions
   * @returns
   */
  private filterByInstanceId<T extends Action>(): UnaryFunction<Observable<T>, Observable<T>> {
    return pipe(
      filter((action) => {
        if (!PagedActionsTypesSet.has(action.type)) {
          return true;
        }

        const instanceId = (action as any).instanceId;

        return this.gridInstanceIds.has(instanceId);
      }),
    );
  }

  private createCacheService(instanceId: string): void {
    const cacheService = new ChunksCache(this.sessionCacheService);
    cacheService.clearCacheIfExistsAsync();
    this.chunkCacheServices.set(instanceId, cacheService);
  }

  private disposeCacheService(instanceId: string): void {
    if (this.chunkCacheServices.has(instanceId)) {
      this.chunkCacheServices.get(instanceId).clearCacheIfExistsAsync();
      this.chunkCacheServices.delete(instanceId);
    }
  }
}
